/**
 * Social Media Manager - Direct Database Implementation
 * 
 * This component implements social media management using direct database access
 * to the Supabase backend for real-time post management and scheduling.
 */

import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, RefreshCw, Grid, List, Loader2, ArrowUp, ArrowDown, HelpCircle } from 'lucide-react'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

// Import direct implementation components
import PostCreateForm from '@/features/social/components/post-create/PostCreateForm'
import PostList from '@/features/social/components/management/PostList'
import PostSkeleton from '@/features/social/components/management/PostSkeleton'
import PostsError from '@/features/social/components/management/PostsError'
import QueueManager from '@/features/social/components/management/QueueManager'
import ProfileManager from '@/features/social/components/management/ProfileManager'
import ContentCalendar from './ContentCalendar'

// Import direct database hooks
import {
  usePosts,
  useProfiles,
  useDeletePost,
  usePublishPost,
  useProcessScheduledPosts,
  useScheduledPosts,
  usePostStatusSubscription,
  type PostFilters,
  type SocialPost
} from '@/features/social/hooks'
import { POST_STATUS } from '@/constants/socialPostStatus'
import { sortPosts, type SortField, type SortOrder } from '@/utils/postSorting'

export default function SocialMediaManagerDirect() {
  const navigate = useNavigate()
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [sortField, setSortField] = useState<SortField>('relevant_date')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  // Direct database hooks
  const { data: profiles = [], isLoading: profilesLoading, refetch: refetchProfiles } = useProfiles()
  const { data: scheduledPosts = [] } = useScheduledPosts()

  // Enable real-time status updates
  const { isConnected, checkForUpdates } = usePostStatusSubscription()

  // Build filters for posts query
  const postFilters: PostFilters = {
    ...(statusFilter !== 'all' && { status: [statusFilter as any] }),
    ...(selectedProfile !== 'all' && { profiles: [selectedProfile] }),
    search: searchQuery
  }

  const {
    data: posts = [],
    isLoading: postsLoading,
    error: postsError,
    refetch: refetchPosts
  } = usePosts(postFilters)

  // Mutations
  const deletePostMutation = useDeletePost()
  const publishPostMutation = usePublishPost()
  const processScheduledMutation = useProcessScheduledPosts()

  // Sort posts
  const sortedPosts = useMemo(() => {
    return sortPosts(posts, { field: sortField, order: sortOrder })
  }, [posts, sortField, sortOrder])

  // Stats calculations
  const stats = {
    total: posts.length,
    draft: posts.filter(p => p.status === POST_STATUS.DRAFT).length,
    scheduled: posts.filter(p => p.status === POST_STATUS.SCHEDULED).length,
    published: posts.filter(p => p.status === POST_STATUS.POSTED).length,
    failed: posts.filter(p => p.status === POST_STATUS.ERROR).length
  }

  // Handle sort change
  const handleSortChange = useCallback((field: SortField) => {
    if (field === sortField) {
      // Toggle order if same field
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')
    } else {
      // New field, default to desc
      setSortField(field)
      setSortOrder('desc')
    }
  }, [sortField])

  // Event handlers
  const handleCreatePost = () => {
    navigate('/marketing/social/create')
  }

  const handleEditPost = (post: SocialPost) => {
    setSelectedPost(post)
    setShowEditDialog(true)
  }

  const handleDeletePost = async (postId: string) => {
    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      await deletePostMutation.mutateAsync(postId)
    }
  }

  const handleDuplicatePost = (post: SocialPost) => {
    // Store the post data in sessionStorage for the create page to pick up
    const duplicateData = {
      content: post.content,
      caption: post.caption || '',
      image_urls: post.image_urls || [],
      hashtags: post.hashtags || [],
      tags: post.tags || []
    }
    sessionStorage.setItem('duplicatePostData', JSON.stringify(duplicateData))
    navigate('/marketing/social/create')
  }

  const handlePublishPost = async (post: SocialPost) => {
    const testMode = !window.confirm('Are you sure you want to publish this post to Instagram? Click OK for production, Cancel for test mode.')
    await publishPostMutation.mutateAsync({ postId: post.id, testMode })
  }

  const handleRetryPost = async (post: SocialPost) => {
    // Retry is essentially publishing again
    const testMode = !window.confirm('Retry publishing this post to Instagram? Click OK for production, Cancel for test mode.')
    await publishPostMutation.mutateAsync({ postId: post.id, testMode })
  }

  const handleViewOnPlatform = (post: SocialPost) => {
    // This would need the Instagram post ID stored in platform_specific_data
    if (post.platform_specific_data?.instagram_post_id) {
      window.open(`https://www.instagram.com/p/${post.platform_specific_data.instagram_post_id}/`, '_blank')
    } else {
      alert('Instagram post ID not available. The post may not have been published successfully.')
    }
  }

  const handleProcessScheduled = async () => {
    await processScheduledMutation.mutateAsync()
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await Promise.all([
        refetchPosts(),
        refetchProfiles()
      ])
    } finally {
      setTimeout(() => setIsRefreshing(false), 500) // Minimum animation time
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Social Media Manager</h1>
          <p className="text-muted-foreground">
            Create, schedule, and publish posts directly to social media
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Real-time connection indicator */}
          <div className={cn(
            "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs",
            isConnected
              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400"
          )}>
            <div className={cn(
              "h-2 w-2 rounded-full",
              isConnected
                ? "bg-green-500 animate-pulse"
                : "bg-yellow-500"
            )} />
            <span className="font-medium">
              {isConnected ? "Live" : "Connecting..."}
            </span>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
          </Button>

          <Button onClick={handleCreatePost}>
            <Plus className="h-4 w-4 mr-2" />
            Create Post
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Posts</CardDescription>
            <CardTitle className="text-2xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Drafts</CardDescription>
            <CardTitle className="text-2xl">{stats.draft}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Scheduled</CardDescription>
            <CardTitle className="text-2xl">{stats.scheduled}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Published</CardDescription>
            <CardTitle className="text-2xl">{stats.published}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Failed</CardDescription>
            <CardTitle className="text-2xl">{stats.failed}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Quick Actions */}
      {scheduledPosts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Ready to Publish</CardTitle>
                <CardDescription>
                  {scheduledPosts.length} posts are due for publishing
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={handleProcessScheduled}
                disabled={processScheduledMutation.isPending}
              >
                {processScheduledMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Process All'
                )}
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="posts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="posts">Posts</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="queues">Queues</TabsTrigger>
          <TabsTrigger value="profiles">Profiles</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-4">
                {/* Search and filters row */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search posts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="DRAFT">Draft</SelectItem>
                        <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                        <SelectItem value="POSTED">Published</SelectItem>
                        <SelectItem value="ERROR">Failed</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={selectedProfile} onValueChange={setSelectedProfile}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Profiles" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Profiles</SelectItem>
                        {profiles.map((profile) => (
                          <SelectItem key={profile.id} value={profile.id}>
                            {profile.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex gap-1 border rounded-md">
                      <Button
                        variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('grid')}
                      >
                        <Grid className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('list')}
                      >
                        <List className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sorting controls */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Sort by:</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="font-semibold mb-1">Sorting Options:</p>
                          <ul className="text-xs space-y-1">
                            <li>• <strong>Smart Sort</strong>: Automatically uses the most relevant date</li>
                            <li>• <strong>Date Created</strong>: When the post was first created</li>
                            <li>• <strong>Schedule Date</strong>: When scheduled posts are set to publish</li>
                            <li>• <strong>Publish Date</strong>: When posts were actually published</li>
                            <li>• <strong>Status</strong>: Groups by post status (Error → Draft → Published)</li>
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={sortField === 'relevant_date' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleSortChange('relevant_date')}
                          className="gap-1"
                        >
                          Smart Sort
                          {sortField === 'relevant_date' && (
                            sortOrder === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-semibold">Smart Sort</p>
                        <p className="text-xs">Uses publish date for published posts,</p>
                        <p className="text-xs">schedule date for scheduled posts,</p>
                        <p className="text-xs">and creation date for drafts</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={sortField === 'created_at' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleSortChange('created_at')}
                          className="gap-1"
                        >
                          Date Created
                          {sortField === 'created_at' && (
                            sortOrder === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-semibold">Date Created</p>
                        <p className="text-xs">Sort all posts by when they were first created,</p>
                        <p className="text-xs">regardless of their current status</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={sortField === 'scheduled_time' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleSortChange('scheduled_time')}
                          className="gap-1"
                        >
                          Schedule Date
                          {sortField === 'scheduled_time' && (
                            sortOrder === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-semibold">Schedule Date</p>
                        <p className="text-xs">Sort by scheduled publish time</p>
                        <p className="text-xs text-orange-400">Note: Posts without a schedule date</p>
                        <p className="text-xs text-orange-400">(like drafts) will appear at the bottom</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={sortField === 'published_at' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleSortChange('published_at')}
                          className="gap-1"
                        >
                          Publish Date
                          {sortField === 'published_at' && (
                            sortOrder === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-semibold">Publish Date</p>
                        <p className="text-xs">Sort by actual publish time</p>
                        <p className="text-xs text-orange-400">Note: Unpublished posts (drafts, scheduled)</p>
                        <p className="text-xs text-orange-400">will appear at the bottom</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={sortField === 'status' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleSortChange('status')}
                          className="gap-1"
                        >
                          Status
                          {sortField === 'status' && (
                            sortOrder === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-semibold">Status Priority</p>
                        <p className="text-xs">Groups posts by status priority:</p>
                        <p className="text-xs">Error → Processing → Scheduled →</p>
                        <p className="text-xs">Draft → Published → Cancelled</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Posts List */}
          {postsError ? (
            <PostsError error={postsError} onRetry={handleRefresh} />
          ) : postsLoading || profilesLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <PostSkeleton key={i} />
              ))}
            </div>
          ) : (
            <PostList
              posts={sortedPosts}
              profiles={profiles}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
              onDuplicate={handleDuplicatePost}
              onPublish={handlePublishPost}
              onRetry={handleRetryPost}
              onViewOnPlatform={handleViewOnPlatform}
            />
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <ContentCalendar />
        </TabsContent>

        <TabsContent value="queues">
          <QueueManager />
        </TabsContent>

        <TabsContent value="profiles">
          <ProfileManager />
        </TabsContent>
      </Tabs>

      {/* Edit Post Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          <PostCreateForm
            initialData={selectedPost || undefined}
            onClose={() => {
              setShowEditDialog(false)
              setSelectedPost(null)
            }}
            onSuccess={() => {
              refetchPosts()
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
